import { useMutation, useQuery } from '@tanstack/vue-query';
import * as Sentry from '@sentry/vue';
import { StatsigSingleton } from '@laam/statsig-client';
import { validatorFactory, validatorFactoryArray } from '@laam/lib/validator';
import type {
	CreateCheckoutResponse,
	GetCheckoutResponse,
	ShippingRatesResponse,
	PaymentProfileResponse,
	TaxRatesResponse,
	PlaceOrderResponse,
	DiscountsObj,
	ShippingRatesResponseOld,
	BinDiscountResponse,
} from '~/types/Checkout.ts';
import {
	CreateCheckoutSchema,
	GetCheckoutSchema,
	ShippingRatesSchema,
	PaymentProfileSchema,
	TaxRatesSchemaNew,
	PlaceOrderSchema,
	ShippingRatesSchemaOld,
	BinDiscountSchema,
} from '~/types/Checkout.ts';
import { USTAXEDSTATES } from '../utils/enums';
import { validateZipCode } from '~/utils/checkout';
import { useEasyPaisaStore } from '~/stores/easypaisa';

const createCheckoutValidator =
	validatorFactory<CreateCheckoutResponse>(CreateCheckoutSchema);

const getCheckoutValidator =
	validatorFactory<GetCheckoutResponse>(GetCheckoutSchema);

const shippingRatesValidator =
	validatorFactoryArray<ShippingRatesResponse>(ShippingRatesSchema);

const shippingRatesValidatorOld = validatorFactory<ShippingRatesResponseOld>(
	ShippingRatesSchemaOld,
);

const paymentProfileValidator =
	validatorFactory<PaymentProfileResponse>(PaymentProfileSchema);

const taxRatesValidator = validatorFactory<TaxRatesResponse>(TaxRatesSchemaNew);
const placeOrderValidator =
	validatorFactory<PlaceOrderResponse>(PlaceOrderSchema);

const binDiscountValidator =
	validatorFactory<BinDiscountResponse>(BinDiscountSchema);
interface Headers {
	[key: string]: string;
}

interface CheckoutStartPayload {
	line_items: {
		quantity: number;
		id: number;
	}[];
}

interface RemoveOOSPayload {
	variant_ids: number[];
}
export interface PlaceOrderPayload {
	hash: string;
	shipping_address: {
		first_name: string;
		last_name: string;
		address1: string;
		address2: string;
		phone: string;
		city: string;
		province: string;
		country: string;
		zip: string;
	};
	billing_address: {
		first_name: string | undefined;
		last_name: string | undefined;
		address1: string | undefined;
		address2: string | undefined;
		phone: string;
		city: string | undefined;
		province: string | undefined;
		country: string | undefined;
		zip: string | undefined;
	};
	note_attributes: {
		name: string;
		value: string;
	}[];

	base_curency_code: string;
	payment_type: string;
	is_hvc: boolean;
	rewards?: DiscountsObj;
	coupon_code?: DiscountsObj;
	credit_card_discount?: DiscountsObj;
	email: string;
	// tags: string;
}

interface CompleteOrderPayload {
	hash: string;
	intentId: string;
	paymentType: string;
	discountsObject: {
		discounts: DiscountsObj[];
	};
	potentialRewards: string;
	baseCurrencyCode: string;
}

export const useCreateCheckoutMutation = (
	loganId: Ref<string | undefined>,
	headers?: Ref<Headers>,
	cartId?: Ref<number | undefined>,
) => {
	const { data, error, isPending, status, mutate } = useMutation({
		mutationKey: ['create_checkout'],
		gcTime: Infinity,
		retry: 3,
		mutationFn: async (payload: CheckoutStartPayload) => {
			const api = createApiInstance();
			const stableId = process.client ? StatsigSingleton.getStableID() : '';
			let url = `v1/checkout/checkout?stable_id=${new Date().getTime()}&request_id=${stableId}`;
			if (cartId?.value) {
				url += `&cart_id=${cartId.value}`;
			}
			try {
				const response = await api.post(url, payload, {
					headers: headers?.value,
				});

				if (response.status === 200 || response.status === 201) {
					try {
						return createCheckoutValidator.verify(response.data);
					} catch (e) {
						const err = new Error(
							`checkout - create-checkout [STABLEID]:${stableId} `,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(err || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Create checkout repsonse validation failed.',
							});
							return scope;
						});
						throw err;
					}
				} else {
					const err = new Error(
						`checkout - create-checkout [STABLEID]:${stableId} `,
					);
					if (response.status !== 422) {
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: response.status,
								response: JSON.stringify(response.data || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Failed to create checkout.',
							});
							return scope;
						});
						throw err;
					}
					throw err;
				}
			} catch (error) {
				Sentry.captureException(
					`checkout - create-checkout [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Something went wrong while creating checkout.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		status,
		isPending,
		mutate,
	};
};

export const useGetCheckoutQuery = (
	checkoutHash: Ref<string>,
	loganId: Ref<string | undefined>,
	storeIdentifier: string,
) => {
	const enabled = computed(() => Boolean(checkoutHash.value));
	const stableId = process.client ? StatsigSingleton.getStableID() : '';

	const { data, error, isLoading, isFetched, refetch, suspense } = useQuery({
		queryKey: ['checkout', checkoutHash, 'loganId', loganId],
		staleTime: 300000, // 5 minutes
		gcTime: 1,
		enabled,
		refetchOnWindowFocus: false,
		refetchInterval: 300000,
		retry: 3,
		queryFn: async () => {
			const api = createApiInstance();
			try {
				const response = await api.get(
					`v1/checkout/checkout/${checkoutHash.value}?stable_id=${new Date().getTime()}&request_id=${stableId}`,
					{
						headers: {
							'store-identifier': storeIdentifier,
						},
					},
				);

				if (response.status === 200) {
					try {
						return getCheckoutValidator.verify(response.data);
					} catch (e) {
						const err = new Error(
							`checkout - get-checkout [STABLEID]:${stableId}`,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(e || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Checkout repsonse validation failed.',
							});
							return scope;
						});
						throw err;
					}
				} else if (response.status === 410) {
					if (process.client && window && window.sessionStorage) {
						sessionStorage.setItem(
							'expiredMessage',
							JSON.stringify({
								title: 'Oops! Checkout link has expired',
								detail: response.data.detail,
							}),
						);
						await navigateTo('/checkout-invalid');
					}
				} else if (response.status === 422) {
					if (process.client && window && window.sessionStorage) {
						sessionStorage.setItem(
							'expiredMessage',
							JSON.stringify({
								title: "Whoops! This checkout link isn't yours.", // eslint-disable-line quotes
								detail: response.data.detail,
							}),
						);
						await navigateTo('/checkout-invalid');
					}
				} else if (response.status !== 200) {
					const err = new Error(
						`checkout - get-checkout [STABLEID]:${stableId} `,
					);
					Sentry.captureException(`get-checkout error - ${err}`, (scope) => {
						scope.setContext('data', {
							error: response.status,
							response: JSON.stringify(response.data || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Failed to fetch checkout.',
						});
						return scope;
					});
					throw err;
				}

				return;
			} catch (error) {
				console.log(error);
				Sentry.captureException(
					`checkout - get-checkout [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Something went wrong while fetching checkout.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		refetch,
		suspense,
		isLoading,
		isFetched,
	};
};

export const useShippingChargesQuery = (
	countryCode: Ref<string>,
	loganId: Ref<string | undefined>,
	shippingPayload: Ref<CheckoutStartPayload | undefined>,
	hash: Ref<string>,
	storeIdentifier: string,
	checkoutDataLoading: Ref<boolean>,
	deliveryMethodsExp: Ref<boolean>,
	stateName: Ref<string>,
) => {
	const enabled = computed(
		() => shippingPayload.value != undefined && !checkoutDataLoading.value,
	);
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	const { data, error, isLoading, isFetched, refetch, suspense } = useQuery({
		staleTime: 300000, // 10 minutes stale time
		retry: 3,
		enabled,
		refetchOnWindowFocus: false,
		refetchInterval: 300000,
		queryKey: [
			'shipping',
			shippingPayload,
			'countryCode',
			countryCode,
			'deliveryMethodsExp',
			deliveryMethodsExp,
			'stateName',
			stateName,
		],
		queryFn: async () => {
			const api = createApiInstance();
			try {
				const response = await api.post(
					`v1/checkout/shipping?hash=${hash.value}&request_id=${stableId}&delivery_methods=${deliveryMethodsExp.value}&state=${stateName.value}`,
					shippingPayload.value,
					{
						headers: {
							'X-Client-Geo-Location': countryCode.value,
							'store-identifier': storeIdentifier,
							country: countryCode.value,
						},
					},
				);
				const respStatus = response.status;
				if (respStatus === 200 || respStatus === 219 || respStatus === 419) {
					try {
						if (deliveryMethodsExp.value) {
							// type === STANDARD should be first
							let data = shippingRatesValidator.verify(response.data);
							// in these 2 cases list will only have one element
							if (respStatus === 219 || respStatus === 419) {
								data[0]!['status'] = respStatus;
							}
							// if response status code is
							data = data.sort((a) => {
								if (a.type === 'STANDARD') {
									return -1;
								}
								return 1;
							});
							return data;
						} else {
							return shippingRatesValidatorOld.verify(response.data);
						}
					} catch (e) {
						const err = new Error(
							`checkout - get-shipping-charges [STABLEID]:${stableId} [RESPSTATUS]:${respStatus}`,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(e || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Shipping charges repsonse validation failed.',
							});
							return scope;
						});
						throw err;
					}
				} else if (response.status !== 200) {
					const err = new Error(
						`checkout - get-shipping-charges [STABLEID]:${stableId}`,
					);
					Sentry.captureException(
						`get-shipping-charges error - ${err}`,
						(scope) => {
							scope.setContext('data', {
								error: response.status,
								response: JSON.stringify(response.data || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Failed to fetch shipping charges.',
								countryCode: countryCode.value,
								haveLineItems: shippingPayload.value?.line_items.length,
							});
							return scope;
						},
					);
					throw err;
				}

				return;
			} catch (error) {
				console.log(error);
				Sentry.captureException(
					`checkout - get-shipping-charges [STABLEID]:${stableId} `,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace:
								'Something went wrong while fetching shipping charges.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		refetch,
		suspense,
		isLoading,
		isFetched,
	};
};

const sortPaymentProfile = (data: PaymentProfileResponse) => {
	const prioirty = [
		'Cash on Delivery (COD)',
		'easy_paisa',
		'Stripe Payment',
		'xPay',
		'xpay-jazz',
		'xpay-easypaisa',
		'Bank Deposit',
	];
	const sorted: PaymentProfileResponse = { valid_methods: [] };
	prioirty.forEach((item) => {
		data.valid_methods.forEach((dataItem) => {
			if (dataItem === item) {
				sorted.valid_methods.push(item);
			}
		});
	});
	return sorted;
};

export const useGetPaymentProfileQuery = (
	countryCode: Ref<string>,
	loganId: Ref<string | undefined>,
	paymentProfilePayload: Ref<CheckoutStartPayload | undefined>,
	hash: Ref<string>,
	storeIdentifier: string,
	baseCurrencyCode: Ref<string>,
	checkoutDataLoading: Ref<boolean>,
) => {
	const enabled = computed(
		() =>
			paymentProfilePayload.value != undefined && !checkoutDataLoading.value,
	);
	const payloadString = computed(() =>
		JSON.stringify(paymentProfilePayload.value),
	);
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	let is_miniapp = false;
	if (typeof window !== 'undefined') {
		try {
			const epStore = useEasyPaisaStore();
			if (epStore?.isEasypaisaMiniapp) {
				is_miniapp = true;
			} else {
				// Fallback
				// @ts-expect-error injected by host mini-app
				is_miniapp = typeof mp !== 'undefined';
			}
		} catch {
			is_miniapp = false;
		}
	}
	const { data, error, isLoading, isFetched, refetch, suspense } = useQuery({
		queryKey: [
			'countryCode',
			countryCode,
			'baseCurrencyCode',
			baseCurrencyCode,
			'payload',
			payloadString,
		],
		staleTime: 300000, // 5 minutes
		gcTime: 1,
		retry: 3,
		refetchOnWindowFocus: false,
		refetchInterval: 300000,
		enabled,
		queryFn: async () => {
			const api = createApiInstance();
			try {
				const response = await api.post(
					`v1/checkout/payment?hash=${hash.value}&request_id=${stableId}&is_mini_app=${is_miniapp}`,
					paymentProfilePayload.value,
					{
						headers: {
							'X-Client-Geo-Location': countryCode.value,
							'store-identifier': storeIdentifier,
							country: countryCode.value,
							base_currency_code: baseCurrencyCode.value,
						},
					},
				);

				if (response.status === 200) {
					try {
						return paymentProfileValidator.verify(
							sortPaymentProfile(response.data),
						);
					} catch (e) {
						const err = new Error(
							`checkout - get-payment-methods [STABLEID]:${stableId} `,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(e || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Get payment methods repsonse validation failed.',
							});
							return scope;
						});
						throw err;
					}
				} else if (response.status !== 200) {
					const err = new Error(
						`checkout - get-payment-methods [STABLEID]:${stableId}`,
					);
					Sentry.captureException(
						`get-payment-methods error - ${err}`,
						(scope) => {
							scope.setContext('data', {
								error: response.status,
								response: JSON.stringify(response.data || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Failed to fetch payment methods.',
								countryCode: countryCode.value,
								haveLineItems: paymentProfilePayload.value?.line_items.length,
							});
							return scope;
						},
					);
					throw err;
				}

				return;
			} catch (error) {
				console.log(error);
				Sentry.captureException(
					`checkout - get-payment-methods [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace:
								'Something went wrong while fetching payment methods.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		refetch,
		suspense,
		isLoading,
		isFetched,
	};
};

export const useGetTaxesQuery = (
	countryCode: Ref<string>,
	loganId: Ref<string | undefined>,
	hash: Ref<string>,
	zipCode: Ref<string>,
	state: Ref<string>,
	storeIdentifier: string,
	checkoutDataLoading: Ref<boolean>,
) => {
	const enabled = computed(
		() =>
			countryCode.value === 'US' &&
			USTAXEDSTATES.includes(state.value) &&
			validateZipCode(zipCode.value, countryCode.value) &&
			!checkoutDataLoading.value,
	);
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	const { data, error, isLoading, isFetched, refetch, suspense } = useQuery({
		queryKey: ['countryCode', countryCode, 'state', state, 'zipCode', zipCode],
		staleTime: 300000, // 5 minutes
		gcTime: 1,
		retry: 3,
		refetchOnWindowFocus: false,
		refetchInterval: 300000,
		enabled,
		queryFn: async () => {
			const api = createApiInstance();
			try {
				const response = await api.get(
					`v1/checkout/tax?hash=${hash.value}&state=${state.value}&zip_code=${zipCode.value}&request_id=${stableId}`,
					{
						headers: {
							'store-identifier': storeIdentifier,
						},
					},
				);

				if (response.status === 200) {
					try {
						return taxRatesValidator.verify(response.data);
					} catch (e) {
						const err = new Error(`checkout - get-tax [STABLEID]:${stableId}`);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(e || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Get tax repsonse validation failed.',
							});
							return scope;
						});
						throw err;
					}
				} else if (response.status !== 200 && response.status !== 404) {
					const err = new Error(`checkout - get-tax [STABLEID]:${stableId}`);
					Sentry.captureException(`get-tax error - ${err}`, (scope) => {
						scope.setContext('data', {
							error: response.status,
							response: JSON.stringify(response.data || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Failed to fetch tax.',
						});
						return scope;
					});
					throw err;
				}

				return;
			} catch (error) {
				console.log(error);
				Sentry.captureException(
					`checkout - get-tax [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Something went wrong while fetching tax.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		refetch,
		suspense,
		isLoading,
		isFetched,
	};
};

export const useRemoveOOSItemsMuation = (
	hash: Ref<string>,
	loganId: Ref<string | undefined>,
	storeIdentifier: string,
) => {
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	const { data, error, isPending, status, mutateAsync } = useMutation({
		mutationKey: ['remove_oos'],
		gcTime: 1,
		retry: 3,
		mutationFn: async (payload: RemoveOOSPayload) => {
			const api = createApiInstance();
			try {
				const response = await api.patch(
					`v1/checkout/remove/items?stable_id=${stableId}&hash=${hash.value}`,
					payload,
					{
						headers: {
							'store-identifier': storeIdentifier,
						},
					},
				);

				if (response.status === 200) {
					try {
						return createCheckoutValidator.verify(response.data);
					} catch (e) {
						const err = new Error(
							`checkout - remove oos item[STABLEID]:${stableId} `,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(err || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Remove OOS item repsonse validation failed.',
							});
							return scope;
						});
						throw err;
					}
				} else {
					const err = new Error(
						`checkout - remove oos item [STABLEID]:${stableId}`,
					);
					Sentry.captureException(err, (scope) => {
						scope.setContext('errors', {
							error: response.status,
							response: JSON.stringify(response.data || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Failed to remove oos item.',
						});
						return scope;
					});
					throw err;
				}
			} catch (error) {
				Sentry.captureException(
					`checkout - remove oos item [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Something went wrong while removing oos item.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		status,
		isPending,
		mutateAsync,
	};
};
export const useUpdateCheckoutMutation = (
	hash: Ref<string>,
	loganId: Ref<string | undefined>,
	storeIdentifier: string,
	cartId: Ref<number | undefined>,
) => {
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	const { data, error, isPending, status, mutateAsync } = useMutation({
		mutationKey: ['update_checkout'],
		gcTime: 1,
		retry: 3,
		mutationFn: async (payload: CheckoutStartPayload) => {
			const api = createApiInstance();
			let url = `v1/checkout/checkout?stable_id=${new Date().getTime()}&hash=${hash.value}&request_id=${stableId}`;
			if (cartId.value) {
				url += `&cart_id=${cartId.value}`;
			}
			try {
				const response = await api.patch(url, payload, {
					headers: {
						'store-identifier': storeIdentifier,
					},
				});

				if (response.status === 200) {
					try {
						return createCheckoutValidator.verify(response.data);
					} catch (e) {
						const err = new Error(
							`checkout - update-checkout [STABLEID]:${stableId} `,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(err || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Update checkout repsonse validation failed.',
							});
							return scope;
						});
						throw err;
					}
				} else {
					const err = new Error(
						`checkout - update-checkout [STABLEID]:${stableId}`,
					);
					Sentry.captureException(err, (scope) => {
						scope.setContext('errors', {
							error: response.status,
							response: JSON.stringify(response.data || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Failed to update checkout.',
						});
						return scope;
					});
					throw err;
				}
			} catch (error) {
				Sentry.captureException(
					`checkout - update-checkout [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Something went wrong while updating checkout.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		status,
		isPending,
		mutateAsync,
	};
};

export const usePlaceOrderMutation = (
	loganId: Ref<string | undefined>,
	storeIdentifier: string,
) => {
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	const { data, error, isPending, status, mutateAsync } = useMutation({
		mutationKey: ['place_order'],
		gcTime: 1,
		// retry place order thrice if error is not 422
		retry: (failureCount, error: { status: number; error: Error }) => {
			if (error.status !== 422) {
				return false;
			}
			return failureCount < 3;
		},
		mutationFn: async (payload: PlaceOrderPayload) => {
			const api = createApiInstance();
			try {
				const response = await api.post(
					`v1/checkout/order?stable_id=${new Date().getTime()}&request_id=${stableId}`,
					payload,
					{
						headers: {
							'store-identifier': storeIdentifier,
						},
					},
				);

				if (response.status === 200) {
					try {
						return placeOrderValidator.verify(response.data);
					} catch (e) {
						const err = new Error(
							`checkout - place-order [STABLEID]:${stableId} `,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(err || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Place order repsonse validation failed.',
							});
							return scope;
						});
						throw { status: response.status, error: e };
					}
				} else {
					const err = new Error(
						`checkout - place-order [STABLEID]:${stableId}`,
					);
					Sentry.captureException(err, (scope) => {
						scope.setContext('errors', {
							error: response.status,
							response: JSON.stringify(response.data || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Failed to place order.',
						});
						return scope;
					});
					throw { status: response.status, error: err, data: response.data };
				}
			} catch (error) {
				Sentry.captureException(
					`checkout - place-order [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Something went wrong while placing order.',
						});
						return scope;
					},
				);
				throw { status: 500, error: error };
			}
		},
	});

	return {
		data,
		error,
		status,
		isPending,
		mutateAsync,
	};
};

export const useCompleteOrdeMutation = (
	loganId: Ref<string | undefined>,
	storeIdentifier: string,
) => {
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	const shouldPoll = ref(false);
	const {
		data,
		error,
		isPending,
		status,
		mutateAsync: completeOrderMutation,
	} = useMutation({
		mutationKey: ['complete_order'],
		gcTime: 1,
		retry: 3,
		mutationFn: async (payload: CompleteOrderPayload) => {
			const api = createApiInstance();
			try {
				const response = await api.post(
					`v1/checkout/complete?hash=${payload.hash}&intent_id=${payload.intentId}&payment_type=${payload.paymentType}&potential_rewards=${payload.potentialRewards}&base_currency_code=${payload.baseCurrencyCode}&request_id=${stableId}`,
					payload.discountsObject,
					{
						headers: {
							'store-identifier': storeIdentifier,
						},
					},
				);
				if (response.status === 413) {
					shouldPoll.value = true;
				} else if (response.status === 200) {
					try {
						shouldPoll.value = false;
						return placeOrderValidator.verify(response.data);
					} catch (e) {
						const err = new Error(
							`checkout - complete-order [STABLEID]:${stableId}`,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(err || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Complete order response validation failed.',
							});
							return scope;
						});
						throw err;
					}
				} else {
					if (response.status !== 402) {
						const err = new Error(
							`checkout - complete-order [STABLEID]:${stableId}`,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: response.status,
								response: JSON.stringify(response.data || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Failed to complete order.',
							});
							return scope;
						});
						throw err;
					}
					shouldPoll.value = false;
					// send status code to frontend
					return {
						...response.data,
						statusCode: response.status,
					};
				}
			} catch (error) {
				Sentry.captureException(
					`checkout - complete-order [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Something went wrong while running complete order.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	const pollCompleteOrder = async (payload: CompleteOrderPayload) => {
		const start = Date.now();
		let result;
		while (Date.now() - start < 2 * 60 * 1000) {
			result = await completeOrderMutation(payload);
			if (!shouldPoll.value) {
				break;
			}
			await new Promise((res) => setTimeout(res, 3000)); // wait 3 seconds
		}
		// send status code to frontend
		// if should poll is true and we are out of loop it means poller kept returning 413
		if (shouldPoll.value) {
			return {
				detail: 'Poller returned 413.',
				statusCode: 413,
			};
		}
		return result as PlaceOrderResponse;
	};

	return {
		data,
		error,
		status,
		isPending,
		mutateAsync: pollCompleteOrder,
	};
};

export const useSendCodOtpMutation = (
	loganId: Ref<string | undefined>,
	storeIdentifier: string,
) => {
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	const { data, error, isPending, status, mutateAsync } = useMutation({
		mutationKey: ['send_cod_verification_otp'],
		gcTime: 1,
		retry: 3,
		mutationFn: async (phoneNumber: string) => {
			const api = createApiInstance();
			try {
				const response = await api.post(
					`v1/checkout/order/verify/send?phone_number=${phoneNumber}&store_id=${storeIdentifier}`,
					{
						headers: {
							'store-identifier': storeIdentifier,
						},
					},
				);

				if (response.status === 200) {
					return true;
				} else {
					if (response.status !== 402) {
						const err = new Error(
							`checkout - send-cod-verification-otp [STABLEID]:${stableId}`,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: response.status,
								response: JSON.stringify(response.data || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Failed to send otp for cod verification.',
							});
							return scope;
						});
						throw err;
					}
					return response.data;
				}
			} catch (error) {
				Sentry.captureException(
					`checkout - send-cod-verification-otp [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace:
								'Something went wrong while running send-cod-verification-otp.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		status,
		isPending,
		mutateAsync,
	};
};

interface CodVerificationPayload {
	phone: string;
	logan_otp: string;
	store_id: string;
	shopifyId: string;
}

export const useEnterCodOtpMutation = (
	loganId: Ref<string | undefined>,
	storeIdentifier: string,
) => {
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	const { data, error, isPending, status, mutateAsync } = useMutation({
		mutationKey: ['enter_cod_verification_otp'],
		gcTime: 1,
		retry: 3,
		mutationFn: async (body: CodVerificationPayload) => {
			let version = 'v1';
			const api = createApiInstance();
			const rawData = sessionStorage.getItem('orderData');
			if (rawData) {
				const parsedData = JSON.parse(rawData);
				const { data } = parsedData;

				version = data?.version;
			}

			const { shopifyId, ...payload } = body;
			let paramUrl: string;

			if (version === 'v2') {
				paramUrl = shopifyId ? `order_number=${shopifyId}` : '';
			} else {
				paramUrl = shopifyId ? `shopify_id=${shopifyId}` : '';
			}

			try {
				const response = await api.post(
					`v1/checkout/order/verify/enter?${paramUrl}`,
					payload,
					{
						headers: {
							'store-identifier': storeIdentifier,
						},
					},
				);

				if (response.status === 200) {
					return true;
				} else {
					if (response.status !== 400) {
						const err = new Error(
							`checkout - enter-cod-verification-otp [STABLEID]:${stableId}`,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: response.status,
								response: JSON.stringify(response.data || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Failed to enter otp for cod verification.',
							});
							return scope;
						});
						throw err;
					}
					return response.data;
				}
			} catch (error) {
				Sentry.captureException(
					`checkout - enter-cod-verification-otp [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace:
								'Something went wrong while running enter-cod-verification-otp.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		status,
		isPending,
		mutateAsync,
	};
};
interface CheckoutMetadataPayload {
	hash: string;
	body: {
		hash: string;
		shipping_address: {
			first_name: string;
			last_name: string;
			address1: string;
			address2: string;
			phone: string;
			city: string;
			province: string;
			country: string;
			zip: string;
		};
		email: string;
	};
}

export const useCheckoutMetaDataMutation = (
	loganId: Ref<string>,
	countryCode: Ref<string>,
) => {
	const stableId = process.client ? StatsigSingleton.getStableID() : '';
	const { data, error, isPending, status, mutate } = useMutation({
		mutationKey: ['checkout_metadata_mutation'],
		gcTime: 1,
		retry: 3,
		mutationFn: async (body: CheckoutMetadataPayload) => {
			const api = createApiInstance();
			try {
				const { hash, ...payload } = body;
				const response = await api.put(
					`v1/checkout/checkout/metadata?hash=${hash}`,
					payload.body,
					{
						headers: {
							'X-Client-Geo-Location': countryCode.value,
						},
					},
				);

				if (response.status === 200) {
					return true;
				} else {
					if (response.status !== 400) {
						const err = new Error(
							`checkout - checkout-metadata-mutation [STABLEID]:${stableId}`,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: response.status,
								response: JSON.stringify(response.data || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Failed to enter otp for cod verification.',
							});
							return scope;
						});
						throw err;
					}
					return response.data;
				}
			} catch (error) {
				Sentry.captureException(
					`checkout - checkout-metadata-mutation [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace:
								'Something went wrong while running checkout-metadata-mutation.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		data,
		error,
		status,
		isPending,
		mutate,
	};
};
interface BinDiscountPayload {
	binNumber: number | null;
	paymentMethod: string;
	binDiscountHash: string;
	orderValue: number;
}
export const useGetBinDiscountMutation = (
	storeIdentifier: number,
	loganId: Ref<string>,
	checkoutHash: Ref<string>,
) => {
	const { mutateAsync } = useMutation({
		gcTime: 1,
		mutationFn: async (body: BinDiscountPayload) => {
			const stableId = process.client ? StatsigSingleton.getStableID() : '';
			const api = createApiInstance();
			try {
				let url = `v1/checkout/discount/bin?&payment_method=${body.paymentMethod}&bin_discount_hash=${body.binDiscountHash}&order_value=${body.orderValue}&checkout_hash=${checkoutHash.value}`;
				if (body.binNumber) {
					url += `&bin_number=${body.binNumber}`;
				}
				const response = await api.get(url, {
					headers: {
						'store-identifier': storeIdentifier,
					},
				});

				if (response.status === 200) {
					try {
						return binDiscountValidator.verify(response.data);
					} catch (e) {
						const err = new Error(
							`checkout - get-bin-discount [STABLEID]:${stableId}`,
						);
						Sentry.captureException(err, (scope) => {
							scope.setContext('errors', {
								error: JSON.stringify(e || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace:
									'Checkout get-bin-discount repsonse validation failed.',
							});
							return scope;
						});
						throw err;
					}
				} else if (response.status !== 200 && response.status !== 422) {
					const err = new Error(
						`checkout - get-bin-discount [STABLEID]:${stableId} `,
					);
					Sentry.captureException(
						`get-bin-discount error - ${err}`,
						(scope) => {
							scope.setContext('data', {
								error: response.status,
								response: JSON.stringify(response.data || {}),
								reqId: Date.now(),
								loganId: loganId.value,
								errorTrace: 'Failed get-bin-discount.',
							});
							return scope;
						},
					);
					throw err;
				}
				return;
			} catch (error) {
				Sentry.captureException(
					`checkout - get-bin-discount [STABLEID]:${stableId}`,
					(scope) => {
						scope.setContext('errors', {
							error: JSON.stringify(error || {}),
							reqId: Date.now(),
							loganId: loganId.value,
							errorTrace: 'Something went wrong while fetching bin discount.',
						});
						return scope;
					},
				);
				throw error;
			}
		},
	});

	return {
		mutateAsync,
	};
};
